import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Archive, ArrowDown, ArrowUp, ArrowUpDown, Pencil, Plus, RefreshCw, Search } from "lucide-react";
import Page from "components/Page";
import SectionCard from "components/SectionCard";
import { useDialog } from "context/DialogContext";
import { useProfile } from "context/ProfileContext";
import ProjectModal from "./components/ProjectModal";
import {
  archiveProject,
  deleteProject,
  fetchClassifications,
  fetchProjectClassificationAssignments,
  fetchProjects,
  replaceProjectClassificationAssignments,
  saveProject,
} from "./services/capitalFlowService";

const STATUS_LABELS = {
  proposed: "Proposed",
  approved: "Approved",
  in_progress: "In Progress",
  completed: "Completed",
  deferred: "Deferred",
  cancelled: "Cancelled",
  archived: "Archived",
};

function currency(value) {
  if (value == null) return "—";
  return Number(value).toLocaleString(undefined, { style: "currency", currency: "USD" });
}

const SORT_VALUES = {
  name: (project) => project.project_name || "",
  status: (project) => STATUS_LABELS[project.status] || project.status || "",
  cost: (project) => Number(project.total_project_cost) || 0,
};

export default function CapitalFlowProjects() {
  const { selectedOrg, profile } = useProfile();
  const { alert, confirm } = useDialog();
  const [projects, setProjects] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [editing, setEditing] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "name", direction: "asc" });

  const visibleProjects = useMemo(() => {
    const query = search.trim().toLocaleLowerCase();
    const filtered = query
      ? projects.filter((project) => {
          const values = [
            project.project_name,
            STATUS_LABELS[project.status] || project.status,
            currency(project.total_project_cost),
          ];
          return values.some((value) => String(value || "").toLocaleLowerCase().includes(query));
        })
      : projects;
    const valueFor = SORT_VALUES[sort.key];
    return [...filtered].sort((left, right) => {
      const leftValue = valueFor(left);
      const rightValue = valueFor(right);
      const comparison = typeof leftValue === "number"
        ? leftValue - rightValue
        : leftValue.localeCompare(rightValue, undefined, { sensitivity: "base", numeric: true });
      return sort.direction === "asc" ? comparison : -comparison;
    });
  }, [projects, search, sort]);

  const toggleSort = (key) => {
    setSort((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };

  const SortIcon = ({ column }) => {
    if (sort.key !== column) return <ArrowUpDown size={12} aria-hidden="true" />;
    return sort.direction === "asc"
      ? <ArrowUp size={12} aria-hidden="true" />
      : <ArrowDown size={12} aria-hidden="true" />;
  };

  const load = useCallback(async () => {
    if (!selectedOrg?.id) {
      setProjects([]);
      return;
    }
    setLoading(true);
    try {
      const [nextProjects, nextClassifications] = await Promise.all([
        fetchProjects(selectedOrg.id, { includeArchived }),
        fetchClassifications(selectedOrg.id),
      ]);
      setProjects(nextProjects);
      setClassifications(
        nextClassifications.filter((classification) =>
          classification.scopes.some((scope) => scope.object_type === "project")
        )
      );
    } catch (error) {
      await alert(error.message, { title: "Unable to Load Projects" });
    } finally {
      setLoading(false);
    }
  }, [alert, includeArchived, selectedOrg?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setAssignments([]);
    setModalOpen(true);
  };

  const openEdit = async (project) => {
    try {
      setAssignments(
        await fetchProjectClassificationAssignments(project.id, selectedOrg.id)
      );
      setEditing(project);
      setModalOpen(true);
    } catch (error) {
      await alert(error.message, { title: "Unable to Load Project" });
    }
  };

  const handleSave = async (project, nextAssignments) => {
    setSaving(true);
    try {
      const saved = await saveProject({
        project,
        orgId: selectedOrg.id,
        tenantId: selectedOrg.tenant_id || profile?.tenant_id,
        userId: profile.id,
      });
      await replaceProjectClassificationAssignments({
        projectId: saved.id,
        assignments: nextAssignments,
        orgId: selectedOrg.id,
        tenantId: selectedOrg.tenant_id || profile?.tenant_id,
        userId: profile.id,
      });
      setModalOpen(false);
      await load();
    } catch (error) {
      await alert(error.message, { title: "Unable to Save Project" });
    } finally {
      setSaving(false);
    }
  };

  const handleArchive = async (project) => {
    const accepted = await confirm(`Archive Project “${project.project_name}”?`, {
      title: "Archive Project",
      okLabel: "Archive",
      cancelLabel: "Cancel",
    });
    if (!accepted) return;
    try {
      await archiveProject({ projectId: project.id, orgId: selectedOrg.id, userId: profile.id });
      await load();
    } catch (error) {
      await alert(error.message, { title: "Unable to Archive Project" });
    }
  };

  const handleDelete = async () => {
    if (!editing?.id) return;
    const accepted = await confirm(
      `Permanently delete Project “${editing.project_name}”? This cannot be undone.`,
      {
        title: "Delete Project",
        okLabel: "Delete",
        cancelLabel: "Cancel",
      }
    );
    if (!accepted) return;
    setSaving(true);
    try {
      await deleteProject({ projectId: editing.id, orgId: selectedOrg.id });
      setModalOpen(false);
      setEditing(null);
      await load();
    } catch (error) {
      await alert(error.message, { title: "Unable to Delete Project" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Page
      title="CapitalFlow - Projects"
      subtitle="Maintain capital projects independently of debt issues."
      showOrganizationSelector
    >
      <SectionCard
        title="Projects"
        subtitle={selectedOrg ? `${visibleProjects.length}${search.trim() ? ` of ${projects.length}` : ""} Project${visibleProjects.length === 1 ? "" : "s"}` : "Select an organization"}
        headerAction={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <label className="relative">
              <Search size={14} className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
              <input
                type="search"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search projects…"
                aria-label="Search projects"
                className="w-52 rounded-md border border-input bg-background py-1.5 pl-7 pr-2 text-sm text-foreground shadow-sm outline-none placeholder:text-muted focus:ring-2 focus:ring-ring"
              />
            </label>
            <label className="flex items-center gap-1.5 text-sm text-muted">
              <input type="checkbox" checked={includeArchived} onChange={(event) => setIncludeArchived(event.target.checked)} />
              Show archived
            </label>
            <button type="button" className="btn btn-ghost" onClick={load} disabled={!selectedOrg || loading} title="Refresh Projects">
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
            </button>
            <button type="button" className="btn btn-primary flex items-center gap-2" onClick={openNew} disabled={!selectedOrg}>
              <Plus size={16} /> New Project
            </button>
          </div>
        }
      >
        {!selectedOrg ? (
          <div className="py-8 text-center text-sm text-muted">Select an organization to manage Projects.</div>
        ) : loading ? (
          <div className="py-8 text-center text-sm text-muted">Loading Projects…</div>
        ) : projects.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
                <tr>
                  <th className="px-2 py-1.5"><button type="button" className="flex items-center gap-1 hover:text-app" onClick={() => toggleSort("name")} aria-sort={sort.key === "name" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>Project Name <SortIcon column="name" /></button></th>
                  <th className="px-2 py-1.5"><button type="button" className="flex items-center gap-1 hover:text-app" onClick={() => toggleSort("status")} aria-sort={sort.key === "status" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>Status <SortIcon column="status" /></button></th>
                  <th className="px-2 py-1.5 text-right"><button type="button" className="ml-auto flex items-center gap-1 hover:text-app" onClick={() => toggleSort("cost")} aria-sort={sort.key === "cost" ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}>Project Cost <SortIcon column="cost" /></button></th>
                  <th className="px-2 py-1.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleProjects.map((project) => (
                  <tr key={project.id} className="border-b border-border/60 last:border-0 hover:bg-surface-2/50">
                    <td className="px-2 py-1.5 font-medium leading-tight text-app">{project.project_name}</td>
                    <td className="px-2 py-1.5 leading-tight">{STATUS_LABELS[project.status] || project.status}</td>
                    <td className="px-2 py-1.5 text-right leading-tight tabular-nums">{currency(project.total_project_cost)}</td>
                    <td className="px-2 py-1"><div className="flex justify-end gap-0.5">
                      <button type="button" className="rounded p-1 text-muted hover:bg-surface-2 hover:text-app" onClick={() => openEdit(project)} title="Edit Project" aria-label={`Edit ${project.project_name}`}><Pencil size={14} /></button>
                      {project.is_active ? <button type="button" className="rounded p-1 text-muted hover:bg-surface-2 hover:text-app" onClick={() => handleArchive(project)} title="Archive Project" aria-label={`Archive ${project.project_name}`}><Archive size={14} /></button> : null}
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!visibleProjects.length ? <div className="py-6 text-center text-xs text-muted">No projects match “{search.trim()}”.</div> : null}
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-muted">No Projects have been created for this organization.</div>
        )}
      </SectionCard>

      {modalOpen ? <ProjectModal project={editing} classifications={classifications} initialAssignments={assignments} saving={saving} onSave={handleSave} onDelete={handleDelete} onClose={() => setModalOpen(false)} /> : null}
    </Page>
  );
}
